import * as React from 'react';
import { useMemo, useState } from 'react';
import { styled } from 'styled-components';
import type { FormikProps } from 'formik';

import { Row } from 'common/components';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import useDataLakeTables from 'data-lake/preview/storage-types/external/hooks/useDataLakeTables';
import { Spinner, ExpandableList, ExpandableListItem, IconButton, Icon } from 'components/common';
import { Button } from 'components/bootstrap';
import type { TableInfo } from 'data-lake/preview/Types';
import type { ExternalDataLakeFormValues } from 'data-lake/preview/storage-types/external/Types';
import TableFieldList from 'data-lake/preview/storage-types/external/TableFieldList';
import useUpdateTableFilter from 'data-lake/preview/storage-types/external/hooks/useUpdateTableFilter';
import { WAHREHOUSE_PREVIEW_FORM_ID } from 'data-lake/preview/storage-types/internal/SearchBar';
import useReexecuteSearch from 'data-lake/preview/hooks/useReexecuteSearch';

const StyledRow = styled(Row)`
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-right: 5px;
  flex: 0.4;
  max-width: 700px;
  min-width: 340px;

  && {
    padding: 7px 9px 6px;
    margin-bottom: 0;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 25px;
  width: 100%;
`;

const StyledExpandableList = styled(ExpandableList)`
  width: 100%;
  overflow: auto;
  flex: 1;
  margin-bottom: 0;
`;

const TableName = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover button {
    display: flex;
  }
`;

const UpdateTableFilterButton = styled(Button)`
  display: none;
  z-index: 1;
  right: 0;
  top: 5px;
  position: absolute;
`;

const StyledP = styled.p`
  margin-bottom: 5px;
`;

const Tables = styled.div`
  position: relative;

  &:hover button {
    display: flex;
  }
`;

const groupTables = (tableList: Array<TableInfo>) => {
  const catalogMap = new Map<string, Map<string, Array<TableInfo>>>();

  // group tables by catalog and schema
  tableList.forEach((table) => {
    const schemaMap = catalogMap.get(table.catalog) ?? new Map();

    const tables = schemaMap.get(table.schema) ?? [];
    schemaMap.set(table.schema, [...tables, table]);

    catalogMap.set(table.catalog, schemaMap);
  });

  return [...catalogMap].map(([catalogName, schemaMap]) => ({
    catalogName,
    schemas: [...schemaMap].map(([schemaName, tables]) => ({ schemaName, tables })),
  }));
};

const initialExpanded = (groupedTables, selectedTable: TableInfo) => {
  if (selectedTable) {
    return {
      catalogs: selectedTable ? [selectedTable.catalog] : [],
      schemas: selectedTable ? [selectedTable.schema] : [],
      tables: selectedTable ? [selectedTable.id] : [],
    };
  }

  if (groupedTables.length === 1) {
    return {
      catalogs: [groupedTables[0].catalogName],
      schemas: groupedTables[0].schemas.length === 1 ? [groupedTables[0].schemas[0].schemaName] : [],
      tables: [],
    };
  }

  return { catalogs: [], schemas: [], tables: [] };
};

type ListProps = {
  searchBarFormRef: React.RefObject<FormikProps<ExternalDataLakeFormValues>>;
  tables: Array<TableInfo>;
};

const List = ({ searchBarFormRef, tables }: ListProps) => {
  const { dataLakeFieldsFetchQuery } = useSelectedDataLake();
  const selectedTable = tables?.find((table) => table.id === dataLakeFieldsFetchQuery?.tableId);
  const groupedTables = useMemo(() => groupTables(tables), [tables]);
  const [expanded, setExpanded] = useState<{
    catalogs: string[];
    schemas: string[];
    tables: string[];
  }>(initialExpanded(groupedTables, selectedTable));
  const reexecuteSearch = useReexecuteSearch();
  const updateTableFilter = useUpdateTableFilter(searchBarFormRef.current?.setFieldValue);

  const handleExpand = (key: 'catalogs' | 'schemas' | 'tables', newValue: string[]) => {
    setExpanded((prev) => ({ ...prev, [key]: newValue }));
  };

  return (
    <>
      <StyledP className="description">
        Set a table as the active filter to select its fields. After changes{' '}
        <Button
          type="submit"
          bsSize="xsmall"
          bsStyle="primary"
          disabled={false}
          onClick={reexecuteSearch}
          form={WAHREHOUSE_PREVIEW_FORM_ID}>
          <Icon name="search" />
          Re-execute
        </Button>{' '}
        the search to see results.
      </StyledP>

      <StyledExpandableList value={expanded.catalogs} onChange={(v) => handleExpand('catalogs', v)}>
        {groupedTables.map(({ catalogName, schemas }) => (
          <ExpandableListItem
            value={catalogName}
            key={catalogName}
            header={<>{catalogName === selectedTable?.catalog ? <b>{catalogName}</b> : catalogName}</>}>
            <ExpandableList value={expanded.schemas} onChange={(v) => handleExpand('schemas', v)}>
              {schemas.map(({ schemaName, tables: schemaTables }) => (
                <ExpandableListItem
                  key={schemaName}
                  value={schemaName}
                  header={schemaName === selectedTable?.schema ? <b>{schemaName}</b> : schemaName}>
                  <ExpandableList value={expanded.tables} onChange={(v) => handleExpand('tables', v)}>
                    {schemaTables.map((table) => (
                      <Tables key={table.id}>
                        {selectedTable?.id !== table.id && (
                          <UpdateTableFilterButton
                            bsStyle="primary"
                            bsSize="xsmall"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateTableFilter(table.id);
                            }}>
                            Update filter
                          </UpdateTableFilterButton>
                        )}
                        <ExpandableListItem
                          value={table.id}
                          header={
                            <TableName>{selectedTable?.id === table.id ? <b>{table.name}</b> : table.name}</TableName>
                          }>
                          <TableFieldList tableId={table.id} isExpanded={expanded.tables.includes(table.id)} />
                        </ExpandableListItem>
                      </Tables>
                    ))}
                  </ExpandableList>
                </ExpandableListItem>
              ))}
            </ExpandableList>
          </ExpandableListItem>
        ))}
      </StyledExpandableList>
    </>
  );
};

type Props = {
  searchBarFormRef: React.RefObject<FormikProps<ExternalDataLakeFormValues>>;
  onClose: () => void;
};

const TableList = ({ searchBarFormRef, onClose }: Props) => {
  const { selectedDataLake } = useSelectedDataLake();
  const { data: tables, isLoading } = useDataLakeTables(selectedDataLake?.id);

  return (
    <StyledRow className="content">
      <Header>
        <h5>Table List</h5>
        <IconButton name="close" onClick={onClose} title="Hide table list" />
      </Header>
      {isLoading ? <Spinner text="Loading Tables..." /> : <List searchBarFormRef={searchBarFormRef} tables={tables} />}
    </StyledRow>
  );
};
export default TableList;
